
export enum Category {
  PHOTO_STUDIO = 'Foto/Video štúdio',
  EVENT_SPACE = 'Event priestor',
  MEETING_ROOM = 'Meeting/konferenčka',
  WORKSHOP = 'Ateliér / dielňa',
  OUTDOOR = 'Exteriér',
  UNIQUE = 'Unikátny priestor'
}

export enum Condition {
  STANDARD = 'Štandard',
  PREMIUM = 'Premium',
  LUXURY = 'Luxury'
}

export enum TransactionType {
  SELL = 'Predaj',
  GIVE = 'Darujem',
  AUCTION = 'Aukcia',
  RENT = 'Prenájom'
}

export enum ListingStyle {
  MODERN = 'Moderný',
  SCANDINAVIAN = 'Škandinávsky',
  INDUSTRIAL = 'Industriálny',
  RUSTIC = 'Rustikálny',
  RETRO = 'Retro/Vintage',
  OTHER = 'Iné'
}

export enum ListingMaterial {
  WOOD = 'Masív/Drevo',
  METAL = 'Kov',
  GLASS = 'Sklo',
  TEXTILE = 'Textil',
  LEATHER = 'Koža',
  PLASTIC = 'Plast',
  RATTAN = 'Ratan',
  BRICK = 'Tehla/Kameň',
  CONCRETE = 'Betón',
  OTHER = 'Iné'
}

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  isVerified: boolean;
  isAdmin?: boolean;
  memberSince: string;
  notify_on_status_change?: boolean;
}

export enum ListingStatus {
  PENDING = 'Čaká na schválenie',
  APPROVED = 'Schválený',
  REJECTED = 'Zamietnutý',
  HIDDEN = 'Skrytý'
}

export interface Seller {
  id: string;
  name: string;
  email?: string; // Email používateľa (voliteľné pre spätnú kompatibilitu)
  isVerified: boolean;
  rating: number;
  ratingCount: number;
  memberSince: string;
  responseRate: string; // e.g. "Odpovedá do hodiny"
  requireProfile?: boolean;
  privacy?: {
    showMemberSince?: boolean;
    showEmail?: boolean;
    showName?: boolean;
    showPhone?: boolean;
  };
}



export interface WeeklyScheduleDay {
  /** Day of week: 0=Sunday, 1=Monday, ..., 6=Saturday */
  dayOfWeek: number;
  /** HH:mm - start time, empty if closed */
  from?: string;
  /** HH:mm - end time, empty if closed */
  to?: string;
  /** Whether this day is available */
  enabled: boolean;
}

export interface WeeklySchedule {
  /** Schedule for each day of the week */
  days: WeeklyScheduleDay[];
  /** Slot duration in minutes (espacero: fixed to 30) */
  slotDurationMinutes: number;
  /** Whether space is closed on holidays */
  closedOnHolidays: boolean;
}

export interface SpaceAddress {
  country?: string;
  city?: string;
  street?: string;
  houseNumber?: string;
  zip?: string;
  note?: string;
  lat?: number;
  lon?: number;
}

export interface SpaceDetails {
  /** Public display name could still be Listing.title; this is for space-specific structured info */
  address?: SpaceAddress;
  terms?: string;

  /** Optional media (hosted in R2). */
  videos?: string[];
  videoUrl?: string;

  /** Email used for Stripe Connect registration (optional). */
  maxGuests?: number;

  /** New: weekly recurring schedule */
  weeklySchedule?: WeeklySchedule;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  logoUrl?: string;
  logoMode?: 'light' | 'dark';
  facebookUrl?: string;
  instagramUrl?: string;
  externalGalleryUrl?: string;
  eventTypes?: string[];
  pricePerHour?: number;
  priceType?: 'hourly' | 'konsumne';
  parkingAvailable?: boolean;
  /** Amenities / equipment available in the space (used for search filters) */
  amenities?: string[];
  contactVisibility?: ContactVisibility;
  bookingMode?: 'schedule' | 'agreement';

  /** Preferred contact methods for inquiries */
  preferredContact?: ('phone' | 'email')[];
  /** Whether to show exact address or just approximate area */
  addressVisibility?: 'exact' | 'approximate';

  /** Matterport 3D Virtual Tour URL */
  matterportUrl?: string;

  /** Google Reviews Integration */
  googlePlaceId?: string;
  googleRating?: number;
  googleRatingsCount?: number;
  googleRatingLastFetched?: string; // ISO string

  /** Technical specs for better AI search performance (similar to betterevent.sk) */
  areaSquareMeters?: number;
  seatingCapacities?: {
    theater?: number;
    school?: number;
    banquet?: number;
    cocktail?: number;
  };
  /** Tags associated with individual image URLs (e.g. { "https://.../1.jpg": ["svadba", "meeting"] }) */
  imageTags?: Record<string, string[]>;
}

export interface ContactVisibility {
  email: 'public' | 'registered_only';
  phone: 'public' | 'registered_only';
  allowPlatformContact?: boolean;
}

export interface Listing {
  id: string;
  slug?: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  category: Category;
  condition: Condition;
  status: ListingStatus;
  transactionType: TransactionType;
  style?: ListingStyle;
  material?: ListingMaterial;
  location: string;
  zipCode?: string;
  images: string[]; // Media
  blurHashes?: string[];
  createdAt: string;
  seller: Seller;
  isFeatured?: boolean; // Topovaný inzerát
  dimensions?: string; // e.g. "200x100x50 cm"
  /** Espacero: details for rentable spaces (stored in D1 as JSON `space_details`) */
  spaceDetails?: SpaceDetails;
  /** Subscription status for the listing */
  subscriptionPlan?: 'monthly' | 'yearly';
  subscriptionStatus?: 'active' | 'expired' | 'pending_payment';
  subscriptionExpiry?: string; // ISO date
  views?: number;
  clicks?: number;
  sellerRequireProfile?: boolean;
  isAdminListing?: boolean;
  management?: ListingManagement;
  /** Provenance/trust columns (admin-curated; NULL = derived by the API). */
  trust?: ListingTrust;
}

/**
 * Mirrors migrations/20260715_add_trust_columns.sql.
 * Admin rows carry the raw Slovak states ('overeny' | 'prevzaty' |
 * 'informativny' | 'caka_na_aktualizaciu' | null); public API responses carry
 * the normalized English enum ('verified_host' | 'claimed_by_host' |
 * 'editorial_profile' | 'pending_update'), so the fields are typed loosely.
 */
export interface ListingTrust {
  source?: string | null;              // 'host_submission' | 'public_sources'
  claimedByOwner?: boolean | null;
  verificationStatus?: string | null;
  lastVerifiedAt?: string | null;
  mediaLicenseStatus?: string | null;  // 'owner_uploaded' | 'consent_granted' | 'public_source' | 'pending_review'
}

export interface ListingManagement {
  managedMode: 'internal' | 'external';
  notificationRouting?: 'owner' | 'contact_only' | 'both';
  suppressInApp?: boolean;
}

export interface FilterState {
  category?: Category;
  minPrice?: number;
  maxPrice?: number;
  transactionType?: TransactionType;
  condition?: Condition[];
  style?: ListingStyle[];
  searchQuery: string;
  location?: string;
  radius?: number;
  minCapacity?: number;
  amenities?: string[];
}

export interface AnalyticsData {
  date: string;
  views: number;
  clicks: number;
}


export interface BlogPost {
  id: number;
  title: string;
  seo_title?: string;
  excerpt: string;
  content: string; // HTML/Markdown content
  image: string;
  author: string;
  date: string;
  type?: 'article' | 'tech';
}

export interface RssItem {
  title: string;
  link: string;
  description: string;
  pubDate: string;
  image: string;
  source: string;   // Computed from URL
  category: string; // Computed from URL
}

export interface Review {
  id: string;
  listingId: string;
  userId: string;
  userName: string;
  rating: number; // 1..5
  comment: string;
  createdAt: string; // ISO
}



export type ApiResult<T> =
  | { success: true; data: T }
  | { success: false; error: string };

export enum VendorCategory {
  CATERING = 'Catering & Občerstvenie',
  PHOTOGRAPHY_VIDEO = 'Foto & Video',
  MUSIC_ENTERTAINMENT = 'Hudobníci & DJs',
  DECORATION_DESIGN = 'Interiér & Dekorácie',
  BEAUTY_STYLING = 'Vizáž & Styling',
  TECHNICAL = 'Technika & AV',
  EVENT_MANAGEMENT = 'Event Management',
  PRINT_DESIGN = 'Tlač & Dizajn',
  TRANSPORT = 'Doprava & Logistika',
  OTHER = 'Iné'
}

export enum VendorPricingTier {
  BASIC = 'basic',
  STANDARD = 'standard',
  PREMIUM = 'premium'
}

export type DemandCategory = 'event' | 'meeting' | 'photo' | 'workshop' | 'party' | 'other';
export type DemandExtra = 'catering' | 'AV' | 'parking' | 'wifi' | 'projector' | 'whiteboard' | 'kitchen' | 'outdoor' | 'own_catering' | 'corkage';
export type DemandStatus = 'open' | 'closed' | 'expired';

export interface Demand {
  id: string;
  user_id?: string;          // only visible to author / admin
  user_name: string;
  city?: string;
  radius_km?: number;        // optional search radius around the city (km)
  city_lat?: number;         // city centre, resolved server-side when a radius is set
  city_lon?: number;
  date_from?: string;
  date_to?: string;
  date_flexibility?: number;
  budget_min?: number;
  budget_max?: number;
  capacity?: number;
  category?: DemandCategory;
  extras?: string;           // JSON array string from DB
  description?: string;
  expires_in_days: number;
  status: DemandStatus;
  created_at: string;
  expires_at: string;
  response_count?: number;   // included in /my endpoint
  contact_email?: string;
  contact_phone?: string;
  email_verified?: number;   // 1 = reply channel confirmed (lazy e-mail verification)
  notified_count?: number;   // number of space owners successfully notified
  my_response_at?: string | null; // owner's response timestamp if already responded
}

// Response from POST /api/demands/assist — AI-assist for the demand form.
// Never persisted; only used to prefill the existing description field.
export interface DemandPlatformStats {
  total: number;
  answered: number;
  totalResponses: number;
  responseRate: number;
  totalNotified?: number;
  cities: { city: string; count: number }[];
}

export interface DemandAssistResponse {
  improvedDescription: string;
  missingInfo: string[];
  suggestedQuestions: string[];
  ownerUsefulSummary: string;
}

export interface DemandResponse {
  id: string;
  demand_id: string;
  owner_id?: string;
  owner_name: string;
  owner_email?: string;
  listing_seller_email?: string;
  listing_id: string;
  listing_title?: string;
  listing_location?: string;
  listing_images?: string;
  listing_price?: number;
  listing_category?: string;
  listing_slug?: string;
  listing_space_details?: string;
  message: string;
  created_at: string;
}

export interface OwnerDemandPrefs {
  owner_id: string;
  notify_enabled: number;
  notify_channel: 'email' | 'push' | 'both';
  filter_cities?: string;      // JSON array string
  filter_categories?: string;  // JSON array string
  filter_budget_min?: number;
  filter_budget_max?: number;
  filter_capacity_min?: number;
  onboarding_done: number;
}

/**
 * What the client sends to PUT /api/owner/demand-prefs. The worker validates
 * the city/category filters with Array.isArray — sending the JSON strings of
 * `OwnerDemandPrefs` made it drop them silently, so every owner kept getting
 * every demand. Keep these as arrays.
 */
export interface OwnerDemandPrefsInput {
  notify_enabled: number;
  notify_channel?: 'email' | 'push' | 'both';
  filter_cities?: string[];
  filter_categories?: string[];
  filter_budget_min?: number;
  filter_budget_max?: number;
  filter_capacity_min?: number;
}

export interface Vendor {
  id: string;
  title: string;
  category: VendorCategory;
  location: string;
  description: string;
  images: string[];
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  facebook?: string;
  instagram?: string;
  pricingTier: VendorPricingTier;
  status: ListingStatus;
  createdAt: string;
  updatedAt: string;
  seller: Seller;
  views?: number;
  clicks?: number;
}

export interface RecentVenueEvent {
  id: string;
  venue_id: string;
  event_title: string;
  event_date: string;
  status: 'verified_by_source' | 'confirmed_by_owner' | string;
  venue_title: string;
  venue_slug: string | null;
  venue_location: string;
  image_url?: string | null;
  instagram_url?: string | null;
  source_url?: string | null;
  source_urls?: string[] | null;
  is_host_managed?: boolean;
}

export interface VenuePastEvent {
  id: string;
  venue_id: string;
  event_title: string;
  event_date: string;
  source_urls: string[];
  source_count: number;
  status: 'candidate' | 'verified_by_source' | 'confirmed_by_owner' | 'rejected' | 'hidden_by_owner';
  created_at: string;
  verified_at?: string | null;
  image_url?: string | null;
  instagram_url?: string | null;
}

export type SocialPlatform = 'facebook' | 'instagram';
export type SocialPostStatus = 'draft' | 'approved' | 'rejected' | 'scheduled' | 'publishing' | 'published' | 'failed' | 'uncertain';
export type SocialMediaType = 'none' | 'image' | 'graphic_card';

export interface SocialPost {
  id: string;
  event_id?: string | null;
  venue_id: string;
  platform: SocialPlatform;
  target_account_id?: string | null;
  status: SocialPostStatus;
  content_text: string;
  media_url?: string | null;
  media_type: SocialMediaType;
  graphic_card_config?: string | null;
  hashtags?: string | null;
  version: number;
  is_approved: number;
  approved_by?: string | null;
  approved_at?: string | null;
  scheduled_for?: string | null;
  published_at?: string | null;
  external_post_id?: string | null;
  external_container_id?: string | null;
  external_permalink?: string | null;
  error_message?: string | null;
  last_attempt_at?: string | null;
  attempts_count: number;
  next_attempt_at?: string | null;
  created_at: string;
  updated_at: string;
  event_title?: string;
  event_date?: string;
  event_status?: string;
  source_urls?: string | string[];
  venue_title?: string;
  venue_slug?: string;
  venue_location?: string;
}

export interface SocialPostHistory {
  id: string;
  post_id: string;
  action: string;
  admin_email?: string | null;
  details?: string | null;
  created_at: string;
}

export interface SocialOverviewData {
  counts: {
    draft: number;
    approved: number;
    scheduled: number;
    publishing: number;
    published: number;
    failed: number;
    uncertain: number;
    rejected: number;
  };
  upcoming: SocialPost[];
  recent_errors: SocialPost[];
  scheduler: {
    scheduler_paused: boolean;
    facebook_paused: boolean;
    instagram_paused: boolean;
    drafts_per_week: number;
    last_run_at?: string | null;
  };
  connections: {
    facebook: { configured: boolean; target_id?: string | null };
    instagram: { configured: boolean; target_id?: string | null };
    last_test?: any;
  };
}
